import { lessonSheetNote } from "@aybuben/client/screens/home.ts";
import { type UnitView } from "@aybuben/core/content/course.ts";
import type { LessonDef } from "@aybuben/core/content/types.ts";
import type { GameState } from "@aybuben/core/state.ts";

import { useCourse } from "../content/context.tsx";
import { useStrings } from "../i18n/index.tsx";
import { COL, UI } from "../theme.ts";
import { KIND_UI } from "./lesson-kinds.tsx";
import { Btn, CloseBtn, Sheet } from "./primitives.tsx";

interface LessonSheetProps {
	u: UnitView;
	lesson: LessonDef;
	st: GameState;
	onClose: () => void;
	onStart: () => void;
	/** Offline, with this lesson's sounds not downloaded. */
	offline?: boolean;
}

/**
 * The only sheet Home has left. The unit overview it replaced is now the
 * always-visible unit header, so this is scoped to one lesson: what it is,
 * whether it is reachable, and how to start it.
 */
export function LessonSheet({ u, lesson, st, onClose, onStart, offline }: LessonSheetProps) {
	const course = useCourse();
	const t = useStrings();
	const { open, blocked, note } = lessonSheetNote(
		st,
		course.views.units(),
		u,
		lesson,
		!!offline,
		t,
	);
	const { nameKey } = KIND_UI[lesson.kind];
	return (
		<Sheet onClose={onClose}>
			<div className="flex items-start justify-between gap-3">
				<div>
					<div className="text-sm font-semibold" style={{ color: COL.mute }}>
						{t("home.unitOrdinal", { n: u.number })} · {u.title}
					</div>
					<div className="text-2xl leading-tight font-extrabold">{t(nameKey)}</div>
				</div>
				<CloseBtn onClick={onClose} />
			</div>
			<p className="mt-3 text-sm leading-relaxed" style={{ color: COL.mute, fontFamily: UI }}>
				{note}
			</p>
			<div className="mt-5">
				<Btn disabled={!open || blocked} onClick={onStart} color={COL.pom}>
					{blocked ? t("home.btnOffline") : open ? t("home.btnStartLesson") : t("home.btnLocked")}
				</Btn>
			</div>
		</Sheet>
	);
}
